<script setup>
import { getCurrentInstance } from 'vue';
import CRManualSelection from './CRManualSelection.vue';
import CRError from './CRError.vue';
import CRProgress from './CRProgress.vue';
import CRRegenerate from './CRRegenerate.vue';
import GenerateContentDoc from '../common/GenerateContentDoc.vue';
import { isDocumentResult } from '@/utils/documentResultValidator';

const instance = getCurrentInstance();

const props = defineProps({
  result: Object,
  isDialog: Boolean,
  enableRegeneration: { type: Boolean, default: true },
  availableOutputsForRegen: Array,
  regeneratingPartial: Boolean,
  isPreviousExecution: Boolean,
  // Document selection state
  selectedDocCount: { type: Number, default: 0 },
  selectedIndices: { type: Array, default: null },
  applyingDocuments: Boolean,
  // Manual selection state
  selectionData: Object,
  selectedItems: Array,
  editedItems: null, // Map instance
  newItems: Array,
  editingItem: [Number, String],
  submittingSelection: Boolean
});

defineEmits([
  'regenerate', 'retry', 'back-to-results',
  // Selection events
  'toggle-item', 'select-all', 'clear-all', 'start-edit', 'save-edit',
  'cancel-edit', 'add-new', 'remove-new', 'update-field', 'submit-selection', 'cancel-selection',
  // Document events
  'expand-all-doc', 'collapse-all-doc', 'select-all-doc', 'clear-all-doc',
  'doc-selection-changed'
]);

defineExpose({
  getDocumentRef(rowId) {
    return instance?.proxy?.$refs[`documentComponentRef_${rowId}`] || null;
  }
});
</script>

<template>
  <div v-if="result" class="cr-result-detail">
    <!-- Unified Header Card -->
    <div class="detail-header-card">
      <div class="detail-header-top">
        <div class="detail-header-main">
          <div class="detail-header-icon" :class="{
            'processing': result.processing,
            'result-success': !result.processing && result.success,
            'result-error': !result.processing && !result.success
          }">
            <v-icon v-if="result.processing" color="white" size="20" class="spin-icon">mdi-cog</v-icon>
            <v-icon v-else-if="result.success" color="white" size="20">mdi-check</v-icon>
            <v-icon v-else color="white" size="20">mdi-alert</v-icon>
          </div>
          <div>
            <h3 class="detail-title">Row {{ result.rowNumber }}</h3>
            <span v-if="result.chainName" class="chain-name-header">{{ result.chainName }}</span>
          </div>
        </div>
        <div class="detail-header-stats">
          <div class="stat-item">
            <span class="stat-value">{{ result.progress || 0 }}%</span>
            <span class="stat-label">Complete</span>
          </div>
        </div>
      </div>

      <!-- Input Parameters Row -->
      <div v-if="result.inputs && (Object.keys(result.inputs.manual || {}).length > 0 || Object.keys(result.inputs.hierarchies || {}).length > 0)" class="detail-header-params">
        <div
          v-for="(value, key) in result.inputs.manual"
          :key="key"
          class="param-tag"
        >
          <span class="param-label">{{ key }}</span>
          <span class="param-value">{{ value }}</span>
        </div>
        <div
          v-for="(value, htId) in result.inputs.hierarchies"
          :key="htId"
          class="param-tag hierarchy"
        >
          <v-icon x-small class="mr-1">mdi-sitemap</v-icon>
          <span class="param-value">{{ value }}</span>
        </div>
      </div>

      <!-- Progress Bar (when processing) -->
      <div v-if="result.processing && !result.awaitingSelection" class="detail-header-progress">
        <div class="progress-status-row">
          <span class="current-step">{{ result.currentStep || 'Initializing...' }}</span>
          <span v-if="result.progressDetails" class="step-count">
            {{ Math.min(result.progressDetails.completed + 1, result.progressDetails.total) }}/{{ result.progressDetails.total }} steps
          </span>
        </div>
        <div class="header-progress-bar">
          <div class="progress-fill" :style="{ width: (result.progress || 0) + '%' }"></div>
        </div>
      </div>
    </div>

    <!-- Manual Selection -->
    <CRManualSelection
      v-if="result.awaitingSelection && selectionData"
      :selection-data="selectionData"
      :selected-items="selectedItems"
      :edited-items="editedItems"
      :new-items="newItems"
      :editing-item="editingItem"
      :submitting="submittingSelection"
      @toggle-item="$emit('toggle-item', $event)"
      @select-all="$emit('select-all')"
      @clear-all="$emit('clear-all')"
      @start-edit="$emit('start-edit', $event)"
      @save-edit="$emit('save-edit', $event)"
      @cancel-edit="$emit('cancel-edit', $event)"
      @add-new="$emit('add-new')"
      @remove-new="$emit('remove-new', $event)"
      @update-field="(index, field, value) => $emit('update-field', index, field, value)"
      @submit="$emit('submit-selection')"
      @cancel="$emit('cancel-selection')"
    />

    <!-- Error Display -->
    <CRError
      v-else-if="result.success === false && result.error && !result.processing"
      :error="result.error"
      :execution-details="result.executionDetails"
      @retry="$emit('regenerate', result, null)"
      @back="$emit('back-to-results')"
    />

    <!-- Result Section -->
    <div v-if="!result.processing && result.success" class="detail-section">
      <div class="section-title">
        <v-icon small>mdi-text-box-check</v-icon>
        Generated Result
        <v-spacer></v-spacer>
        <CRRegenerate
          v-if="enableRegeneration"
          :result="result"
          :available-outputs="availableOutputsForRegen"
          :regenerating="regeneratingPartial"
          :is-previous-execution="isPreviousExecution"
          @regenerate="(r, key) => $emit('regenerate', r, key)"
        />
      </div>
      <div class="section-content">
        <!-- Document Result -->
        <div v-if="isDocumentResult(result)" class="document-result">
          <div class="document-actions-header">
            <div class="document-selection-info">
              {{ selectedDocCount }} of {{ result.result.populatedData.length }} items selected
            </div>
            <div class="document-actions">
              <v-btn small color="grey darken-1" @click="$emit('expand-all-doc')" :disabled="applyingDocuments">
                <v-icon small left>mdi-arrow-expand-vertical</v-icon>
                Expand All
              </v-btn>
              <v-btn small color="grey darken-1" @click="$emit('collapse-all-doc')" :disabled="applyingDocuments">
                <v-icon small left>mdi-arrow-collapse-vertical</v-icon>
                Collapse All
              </v-btn>
              <v-btn small color="primary" @click="$emit('select-all-doc')" :disabled="applyingDocuments || selectedDocCount === result.result.populatedData.length">
                <v-icon small left>mdi-check-all</v-icon>
                Select All
              </v-btn>
              <v-btn small color="grey darken-1" @click="$emit('clear-all-doc')" :disabled="applyingDocuments || selectedDocCount === 0">
                <v-icon small left>mdi-close-circle-outline</v-icon>
                Clear All
              </v-btn>
            </div>
          </div>
          <div :class="isDialog ? 'document-items-list-dialog' : 'document-items-list'">
            <GenerateContentDoc
              :ref="`documentComponentRef_${result.rowId}`"
              :key="`documentComponentRef_${result.rowId}`"
              :document="result"
              :selected-indices="selectedIndices"
              @selectionChanged="(data) => $emit('doc-selection-changed', result.rowId, data)"
            />
          </div>
        </div>

        <!-- Text/JSON Result -->
        <div v-else class="text-result-streamlined">
          {{ result.result || 'No result generated' }}
        </div>
      </div>
    </div>

    <!-- Additional Outputs -->
    <div v-if="result.success && result.additionalOutputs && Object.keys(result.additionalOutputs).length > 0" class="detail-section">
      <v-expansion-panels flat class="additional-outputs-expansion">
        <v-expansion-panel class="additional-outputs-panel">
          <v-expansion-panel-header class="additional-outputs-header">
            <div class="additional-outputs-header-content">
              <v-icon small>mdi-code-braces</v-icon>
              <span>Additional Outputs</span>
              <v-chip x-small color="primary" outlined>
                {{ Object.keys(result.additionalOutputs).length }}
              </v-chip>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="additional-outputs-content">
            <div class="additional-outputs-body">
              <table class="input-table">
                <tr v-for="(value, key) in result.additionalOutputs" :key="key">
                  <td>{{ key }}</td>
                  <td class="output-value-cell">{{ value }}</td>
                </tr>
              </table>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>

<style scoped>
.cr-result-detail {
  width: 100%;
}
.detail-header-card {
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 16px;
}
.detail-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.detail-header-main {
  display: flex;
  align-items: center;
  gap: 12px;
}
.detail-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.detail-header-icon.processing { background: #1976D2; }
.detail-header-icon.result-success { background: #4CAF50; }
.detail-header-icon.result-error { background: #f44336; }
.detail-title {
  margin: 0;
  font-size: 16px;
}
.chain-name-header {
  font-size: 12px;
  color: #666;
}
.detail-header-stats {
  text-align: center;
}
.stat-value {
  font-size: 18px;
  font-weight: bold;
}
.stat-label {
  font-size: 11px;
  color: #666;
  display: block;
}
.detail-header-params {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}
.param-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 12px;
}
.param-tag.hierarchy {
  background: #E8EAF6;
}
.param-label {
  font-weight: 600;
  margin-right: 4px;
}
.detail-header-progress {
  margin-top: 12px;
}
.progress-status-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-size: 12px;
}
.header-progress-bar {
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #1976D2, #42A5F5);
  transition: width 0.3s;
}
.detail-section {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
}
.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  font-weight: 600;
  border-bottom: 1px solid #e0e0e0;
}
.section-content {
  padding: 16px;
}
.document-actions {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.document-selection-info {
  font-size: 12px;
  color: #666;
}
.text-result-streamlined {
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 13px;
  padding: 12px;
  background: #fafafa;
  border-radius: 6px;
}
.output-value-cell {
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 12px;
}
.additional-outputs-body {
  padding: 16px;
}
.spin-icon {
  animation: spin 2s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
